import React, { useEffect, useState } from "react";
import { AlertTriangle, Check, Loader2, RotateCw } from "lucide-react";
import { drw } from "./api";

/** DRW-LOAD-A1 — what the background job is doing, in words that keep moving.
 *
 *  Work in progress is never an error. A slow step shows its own clock ("14s on this step") so it
 *  is visibly alive; a status check that times out is retried quietly and only mentioned after
 *  several tries in a row; and the only thing that can show a failure is the JOB saying it failed.
 */
const secs = (ms) => (ms < 60000 ? `${Math.round(ms / 1000)}s` : `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`);

export default function IngestProgress({ job, misses = 0, onRetry }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  if (!job) return null;

  const steps = job.steps || [];
  const failed = job.status === "failed";
  const current = steps.find((s) => s.status === "running") || steps.find((s) => s.status === "failed")
    || [...steps].reverse().find((s) => s.status === "done");
  const done = steps.filter((s) => s.status === "done").length;
  const within = current && current.total ? current.done / current.total : 0;
  const overall = Math.min(100, Math.round(((done + (current?.status === "running" ? within : 0)) / Math.max(steps.length, 1)) * 100));
  const started = Date.parse(job.created_at || "") || now;
  const stepAt = Date.parse(current?.at || job.updated_at || "") || now;

  return (
    <div className="rounded-lg border bg-white p-3 space-y-2" data-testid="ingest-progress">
      <div className="flex items-center gap-2">
        {failed ? <AlertTriangle className="w-4 h-4 text-red-600" />
          : job.status === "ready" ? <Check className="w-4 h-4 text-green-700" />
            : <Loader2 className="w-4 h-4 animate-spin text-orange-600" />}
        <div className="text-sm font-semibold" data-testid="ingest-step-label">
          {failed ? `${job.failed_step_label || "A step"} failed`
            : job.status === "ready" ? "Done"
              : current?.label || "Starting"}
          {!failed && current?.total ? ` — ${current.label === "Splitting sheets" ? "sheet" : "page"} ${current.done} of ${current.total}` : ""}
        </div>
        <div className="ml-auto text-xs text-neutral-500 tabular-nums" data-testid="ingest-elapsed">
          {secs(now - started)} total · {secs(now - stepAt)} on this step
        </div>
      </div>

      <div className="h-2 rounded-full bg-neutral-200 overflow-hidden">
        <div className={`h-full transition-all duration-500 ${failed ? "bg-red-500" : "bg-orange-500"}`}
             style={{ width: `${failed ? 100 : Math.max(overall, 3)}%` }} data-testid="ingest-bar" />
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
        {steps.map((s) => (
          <span key={s.key} data-testid={`ingest-step-${s.key}`}
                className={s.status === "done" ? "text-green-800"
                  : s.status === "running" ? "text-orange-700 font-semibold"
                    : s.status === "failed" ? "text-red-700 font-semibold" : "text-neutral-400"}>
            {s.label.toLowerCase()}
            {s.total ? ` ${s.done}/${s.total}` : ""}
            {s.status === "done" ? " ✓" : s.status === "running" ? "…" : ""}
          </span>
        ))}
      </div>

      {!failed && job.status !== "ready" && (
        <div className="text-[11px] text-neutral-500" data-testid="ingest-reassure">
          This runs on the server — you can close this and come back; the work carries on.
          {misses > 1 ? ` The status check is slow (${misses} tries) — still trying.` : ""}
        </div>
      )}
      {failed && (
        <div className="space-y-2">
          <div className="text-xs text-red-800" data-testid="ingest-error">{job.error || "No reason was recorded."}</div>
          {onRetry && (
            <button type="button" onClick={onRetry} data-testid="ingest-retry"
                    className="h-8 px-3 rounded-md bg-neutral-900 text-white text-xs font-semibold inline-flex items-center gap-1">
              <RotateCw className="w-3.5 h-3.5" /> Carry on from {(job.failed_step_label || "that step").toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
